<script setup lang="ts">
import { computed } from "vue";
import {
  UiCard,
  UiChip,
  UiIcon,
  UiInfoBox,
  UiLink,
  UiStandardButton,
  UiText,
} from "@shopexperts/shared";
import {
  formatServicePeriod,
  formatSubscriptionAmount,
  formatSubscriptionDate,
  getExpertSubscriptionDisplayState,
  type ExpertSubscriptionOverview,
  type SubscriptionStatusTone,
} from "@/app/composables/expert-billing/expertSubscription";

const props = defineProps<{
  accountType: string;
  memberSince: string;
  overview: ExpertSubscriptionOverview | null | undefined;
  isLoading: boolean;
  error: unknown;
  isOpeningPortal: boolean;
  portalError: string | null;
}>();

const emit = defineEmits<{
  manage: [];
  retry: [];
}>();

const display = computed(() =>
  getExpertSubscriptionDisplayState(props.overview),
);
const invoices = computed(
  () => props.overview?.current_subscription?.recent_invoices ?? [],
);
const isManaged = computed(() =>
  Boolean(props.overview?.current_subscription?.is_managed),
);
const scheduledTransition = computed(() => display.value.scheduledTransition);

function statusChipVariant(tone: SubscriptionStatusTone) {
  if (tone === "success") return "success" as const;
  if (tone === "warning") return "warn" as const;
  if (tone === "info") return "outline" as const;
  return "neutral" as const;
}

function invoiceStatusVariant(status: string) {
  if (status === "paid") return "success" as const;
  if (["issued", "open", "past_due"].includes(status)) return "warn" as const;
  return "neutral" as const;
}

function invoiceAmount(
  amountCents: number,
  discountCents: number | null,
  currency: string,
) {
  return formatSubscriptionAmount(
    Math.max(0, amountCents - (discountCents ?? 0)),
    currency,
  );
}
</script>

<template>
  <UiCard padding="lg" data-testid="expert-subscription-summary">
    <div class="flex flex-col gap-8">
      <header
        class="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between"
      >
        <div class="flex min-w-0 items-start gap-4">
          <div
            class="flex size-11 shrink-0 items-center justify-center rounded-full bg-se-gray-100"
          >
            <UiIcon name="dollar-circle" class="size-5 text-se-dark" />
          </div>

          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-3">
              <UiText as="h2" variant="h3" text="Subscription & billing" />
              <UiChip
                v-if="!isLoading && !error"
                :text="display.statusLabel"
                :variant="statusChipVariant(display.statusTone)"
                size="compact"
                rounded="full"
              />
            </div>
            <UiText
              as="p"
              variant="p-small"
              class="mt-1 max-w-2xl text-se-gray-700"
              :text="
                isManaged
                  ? 'Review your plan, renewal coverage, and invoice history. Stripe securely handles payment methods and subscription changes.'
                  : scheduledTransition
                    ? 'Your current paid coverage stays active until your scheduled Stripe plan begins.'
                    : 'Review your account type, current plan, and paid-through date.'
              "
            />
          </div>
        </div>

        <UiStandardButton
          v-if="!isLoading && !error && isManaged && display.canManage"
          class="w-full shrink-0 sm:w-auto sm:min-w-[196px]"
          size="lg"
          variant="primary"
          icon="settings-01"
          :label="isOpeningPortal ? 'Opening Stripe…' : 'Manage subscription'"
          :disabled="isOpeningPortal"
          @click="emit('manage')"
        />
      </header>

      <UiInfoBox
        v-if="portalError"
        variant="error"
        title="Stripe billing could not be opened"
      >
        <UiText as="p" variant="p-small" :text="portalError" />
      </UiInfoBox>

      <div
        v-if="isLoading"
        class="flex items-center justify-center gap-3 border-y border-se-gray-200 py-14"
      >
        <div
          class="size-5 animate-spin rounded-full border-2 border-se-gray-300 border-t-se-dark"
        />
        <UiText
          as="p"
          variant="p-small"
          class="text-se-gray-700"
          text="Loading subscription details…"
        />
      </div>

      <div
        v-else-if="error"
        class="border-y border-se-gray-200 py-10 text-center"
      >
        <UiText
          as="h3"
          variant="h5"
          text="Subscription details are unavailable"
        />
        <UiText
          as="p"
          variant="p-small"
          class="mx-auto mt-1 max-w-lg text-se-gray-700"
          text="We couldn’t load the latest billing state. Your Stripe subscription has not been changed."
        />
        <UiStandardButton
          class="mt-5"
          label="Try again"
          variant="outline"
          size="md"
          @click="emit('retry')"
        />
      </div>

      <template v-else>
        <template v-if="!isManaged">
          <div
            class="grid gap-x-8 gap-y-6 border-t border-se-gray-200 pt-6 sm:grid-cols-2 lg:grid-cols-4"
            data-testid="manual-subscription-summary"
          >
            <div>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                text="Account type"
              />
              <UiText
                as="p"
                variant="p"
                class="mt-1 font-semibold text-se-dark"
                :text="accountType"
              />
            </div>
            <div>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                text="Current plan"
              />
              <UiText
                as="p"
                variant="p"
                class="mt-1 font-semibold text-se-dark"
                :text="display.planName"
              />
            </div>
            <div>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                :text="display.dateLabel"
              />
              <UiText
                as="p"
                variant="p"
                class="mt-1 font-semibold text-se-dark"
                :text="display.dateValue"
              />
            </div>
            <div>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                text="ShopExpert since"
              />
              <UiText
                as="p"
                variant="p"
                class="mt-1 font-semibold text-se-dark"
                :text="memberSince"
              />
            </div>
          </div>

          <section
            v-if="scheduledTransition"
            class="flex flex-col gap-5 border-t border-se-gray-200 pt-6"
            data-testid="scheduled-subscription-transition"
          >
            <UiInfoBox variant="info" title="Your next plan is scheduled">
              <UiText
                as="p"
                variant="p-small"
                :text="`You’ll keep ${display.planName} through ${display.dateValue}. ${scheduledTransition.nextPlanName} starts on ${scheduledTransition.startsAt}, when Stripe is scheduled to collect the first payment.`"
              />
            </UiInfoBox>

            <div class="grid gap-x-8 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <UiText
                  as="span"
                  variant="caption-normal"
                  class="text-se-gray-600"
                  text="Next plan"
                />
                <UiText
                  as="p"
                  variant="p"
                  class="mt-1 font-semibold text-se-dark"
                  :text="scheduledTransition.nextPlanName"
                />
                <UiText
                  v-if="scheduledTransition.optionLabel"
                  as="p"
                  variant="caption-normal"
                  class="mt-0.5 text-se-gray-600"
                  :text="scheduledTransition.optionLabel"
                />
              </div>
              <div>
                <UiText
                  as="span"
                  variant="caption-normal"
                  class="text-se-gray-600"
                  text="Starts & first charge"
                />
                <UiText
                  as="p"
                  variant="p"
                  class="mt-1 font-semibold text-se-dark"
                  :text="scheduledTransition.startsAt"
                />
              </div>
              <div>
                <UiText
                  as="span"
                  variant="caption-normal"
                  class="text-se-gray-600"
                  text="Recurring price"
                />
                <div class="mt-1 flex flex-wrap items-baseline gap-2">
                  <UiText
                    as="span"
                    variant="p"
                    class="font-semibold text-se-dark"
                    :text="scheduledTransition.amount ?? 'Confirmed in Stripe'"
                  />
                  <UiText
                    v-if="scheduledTransition.cadence"
                    as="span"
                    variant="caption-normal"
                    class="text-se-gray-600"
                    :text="scheduledTransition.cadence"
                  />
                </div>
              </div>
            </div>
          </section>
        </template>

        <template v-else>
          <UiInfoBox
            v-if="display.statusMessage"
            :variant="display.statusTone === 'warning' ? 'warning' : 'info'"
            :title="display.statusLabel"
          >
            <UiText as="p" variant="p-small" :text="display.statusMessage" />
          </UiInfoBox>

          <section class="border-y border-se-gray-200 py-6">
            <div
              class="flex flex-col gap-5 md:flex-row md:items-end md:justify-between"
            >
              <div class="min-w-0">
                <UiText
                  as="span"
                  variant="caption-normal"
                  class="text-se-gray-600"
                  text="Current plan"
                />
                <UiText
                  as="h3"
                  variant="h2"
                  class="mt-1"
                  :text="display.planName"
                />
                <UiText
                  v-if="display.optionLabel"
                  as="p"
                  variant="p-small"
                  class="mt-1 text-se-gray-700"
                  :text="display.optionLabel"
                />
              </div>

              <div v-if="display.amount" class="md:text-right">
                <UiText as="p" variant="h3" :text="display.amount" />
                <UiText
                  v-if="display.cadence"
                  as="p"
                  variant="p-small"
                  class="mt-1 text-se-gray-700"
                  :text="display.cadence"
                />
              </div>
            </div>
          </section>

          <div class="grid gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                :text="display.dateLabel"
              />
              <UiText
                as="p"
                variant="p"
                class="mt-1 font-semibold text-se-dark"
                :text="display.dateValue"
              />
            </div>
            <div>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                text="Current service period"
              />
              <UiText
                as="p"
                variant="p-small"
                class="mt-1 font-semibold text-se-dark"
                :text="display.servicePeriod"
              />
            </div>
            <div>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                text="Account type"
              />
              <UiText
                as="p"
                variant="p"
                class="mt-1 font-semibold text-se-dark"
                :text="accountType"
              />
            </div>
            <div>
              <UiText
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                text="ShopExpert since"
              />
              <UiText
                as="p"
                variant="p"
                class="mt-1 font-semibold text-se-dark"
                :text="memberSince"
              />
            </div>
          </div>

          <section>
            <div
              class="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between"
            >
              <div>
                <UiText as="h3" variant="h4" text="Invoice history" />
                <UiText
                  as="p"
                  variant="p-small"
                  class="mt-1 text-se-gray-700"
                  text="Your latest subscription charges and the service period each payment covers."
                />
              </div>
              <UiText
                v-if="invoices.length"
                as="span"
                variant="caption-normal"
                class="text-se-gray-600"
                :text="`${invoices.length} recent invoice${invoices.length === 1 ? '' : 's'}`"
              />
            </div>

            <div
              v-if="invoices.length === 0"
              class="border-y border-dashed border-se-gray-300 py-10 text-center"
            >
              <UiText as="h4" variant="h6" text="No invoices yet" />
              <UiText
                as="p"
                variant="p-small"
                class="mt-1 text-se-gray-700"
                text="Paid subscription invoices will appear here after Stripe confirms them."
              />
            </div>

            <div v-else class="overflow-x-auto border-t border-se-gray-200">
              <table
                class="w-full min-w-[860px] table-auto border-collapse text-left"
              >
                <thead>
                  <tr class="border-b border-se-gray-200">
                    <th class="py-3 pr-6">
                      <UiText
                        as="span"
                        variant="caption-normal"
                        class="text-se-gray-600"
                        text="Invoice"
                      />
                    </th>
                    <th class="px-4 py-3">
                      <UiText
                        as="span"
                        variant="caption-normal"
                        class="text-se-gray-600"
                        text="Amount"
                      />
                    </th>
                    <th class="px-4 py-3">
                      <UiText
                        as="span"
                        variant="caption-normal"
                        class="text-se-gray-600"
                        text="Status"
                      />
                    </th>
                    <th class="px-4 py-3">
                      <UiText
                        as="span"
                        variant="caption-normal"
                        class="text-se-gray-600"
                        text="Service period"
                      />
                    </th>
                    <th class="px-4 py-3">
                      <UiText
                        as="span"
                        variant="caption-normal"
                        class="text-se-gray-600"
                        text="Paid"
                      />
                    </th>
                    <th class="py-3 pl-4 text-right">
                      <UiText
                        as="span"
                        variant="caption-normal"
                        class="text-se-gray-600"
                        text="Receipt"
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="invoice in invoices"
                    :key="invoice.id"
                    class="border-b border-se-gray-200 last:border-b-0"
                  >
                    <td class="py-4 pr-6">
                      <div class="flex items-center gap-3">
                        <div
                          class="flex size-9 shrink-0 items-center justify-center rounded-md bg-se-gray-100"
                        >
                          <UiIcon
                            name="file-placeholder"
                            class="size-4 text-se-gray-700"
                          />
                        </div>
                        <div>
                          <UiText
                            as="p"
                            variant="p-small"
                            class="font-semibold text-se-dark"
                            :text="`Invoice #${invoice.id}`"
                          />
                          <UiText
                            as="p"
                            variant="caption-normal"
                            class="mt-0.5 capitalize text-se-gray-600"
                            :text="
                              invoice.invoice_type === 'managed'
                                ? 'Stripe managed'
                                : invoice.invoice_type
                            "
                          />
                        </div>
                      </div>
                    </td>
                    <td class="px-4 py-4">
                      <UiText
                        as="span"
                        variant="p-small"
                        class="font-semibold tabular-nums text-se-dark"
                        :text="
                          invoiceAmount(
                            invoice.amount_cents,
                            invoice.discount_cents,
                            invoice.currency,
                          )
                        "
                      />
                    </td>
                    <td class="px-4 py-4">
                      <UiChip
                        :text="invoice.provider_status || invoice.status"
                        :variant="
                          invoiceStatusVariant(
                            invoice.provider_status || invoice.status,
                          )
                        "
                        size="xs"
                        rounded="full"
                      />
                    </td>
                    <td class="px-4 py-4">
                      <UiText
                        as="span"
                        variant="p-small"
                        class="text-se-gray-700"
                        :text="
                          formatServicePeriod(
                            invoice.service_period_starts_at,
                            invoice.service_period_ends_at,
                          )
                        "
                      />
                    </td>
                    <td class="px-4 py-4">
                      <UiText
                        as="span"
                        variant="p-small"
                        class="text-se-gray-700"
                        :text="formatSubscriptionDate(invoice.paid_at)"
                      />
                    </td>
                    <td class="py-4 pl-4 text-right">
                      <UiLink
                        v-if="invoice.invoice_url"
                        :href="invoice.invoice_url"
                        label="View invoice"
                        variant="p-small"
                        external="with-arrow"
                      />
                      <UiText
                        v-else
                        as="span"
                        variant="p-small"
                        class="text-se-gray-500"
                        text="Unavailable"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>
        </template>
      </template>
    </div>
  </UiCard>
</template>
